import { useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/hooks/use-toast"
import { useQuery, useMutation } from "@tanstack/react-query"
import { apiRequest, queryClient } from "@/lib/queryClient"
import type { GeneratedPost } from "@shared/schema"
import { 
  Facebook, 
  Instagram, 
  Linkedin, 
  Globe,
  Check, 
  X, 
  Clock,
  ExternalLink,
  Hash,
  MessageSquare,
  Calendar,
  Eye,
  ThumbsUp,
  ThumbsDown
} from "lucide-react"
import { format } from "date-fns"
import { de } from "date-fns/locale"

const platformConfig = {
  facebook: {
    name: "Facebook",
    icon: Facebook,
    color: "bg-blue-600",
    textColor: "text-blue-600"
  },
  instagram: {
    name: "Instagram", 
    icon: Instagram,
    color: "bg-pink-600",
    textColor: "text-pink-600"
  },
  linkedin: {
    name: "LinkedIn",
    icon: Linkedin, 
    color: "bg-blue-700",
    textColor: "text-blue-700"
  },
  google_business: {
    name: "Google My Business",
    icon: Globe,
    color: "bg-green-600", 
    textColor: "text-green-600"
  },
  twitter: {
    name: "Twitter",
    icon: MessageSquare,
    color: "bg-sky-500",
    textColor: "text-sky-500"
  }
} as const

const statusConfig = {
  pending: { label: "Ausstehend", color: "bg-yellow-100 text-yellow-800" },
  approved: { label: "Genehmigt", color: "bg-green-100 text-green-800" },
  rejected: { label: "Abgelehnt", color: "bg-red-100 text-red-800" },
  draft: { label: "Entwurf", color: "bg-gray-100 text-gray-800" },
  scheduled: { label: "Geplant", color: "bg-blue-100 text-blue-800" },
  posted: { label: "Veröffentlicht", color: "bg-green-100 text-green-800" },
  failed: { label: "Fehlgeschlagen", color: "bg-red-100 text-red-800" }
}

export function ContentLibrary() {
  const [selectedPlatform, setSelectedPlatform] = useState<string>("all")
  const [selectedStatus, setSelectedStatus] = useState<string>("all")
  const [reviewPost, setReviewPost] = useState<GeneratedPost | null>(null)
  const [reviewNotes, setReviewNotes] = useState("")

  const { toast } = useToast()

  // Fetch generated posts
  const { data: generatedPosts = [], isLoading } = useQuery<GeneratedPost[]>({
    queryKey: ["/api/generated-posts"],
    refetchInterval: 30000, // Refresh every 30 seconds for real-time updates
  })

  // Approve/reject post mutation
  const approvePostMutation = useMutation({
    mutationFn: async ({ postId, approval }: { postId: string; approval: { approvalStatus: "approved" | "rejected"; approvalNotes?: string } }) => {
      // Fix: apiRequest signature is (method, url, data) — the args were swapped, breaking every
      // approve/reject from this page (the fetch got "POST" as its URL).
      return apiRequest("POST", `/api/generated-posts/${postId}/approve`, approval)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/generated-posts"] })
      setReviewPost(null)
      setReviewNotes("")
      toast({
        title: "✅ Post überprüft",
        description: "Der Post-Status wurde erfolgreich aktualisiert.",
      })
    },
    onError: (error: any) => {
      toast({
        title: "❌ Fehler",
        description: error.message || "Beim Überprüfen des Posts ist ein Fehler aufgetreten.",
        variant: "destructive",
      })
    },
  })

  // Filter posts by platform and status
  const filteredPosts = generatedPosts.filter((post) => {
    const platformMatch = selectedPlatform === "all" || post.platform === selectedPlatform
    const statusMatch = selectedStatus === "all" || post.approvalStatus === selectedStatus
    return platformMatch && statusMatch
  })

  // Group posts by platform for the overview
  const postsByPlatform = generatedPosts.reduce((acc: Record<string, GeneratedPost[]>, post) => {
    if (!acc[post.platform]) {
      acc[post.platform] = []
    }
    acc[post.platform].push(post)
    return acc
  }, {})

  const handleApprovePost = (approval: "approved" | "rejected") => {
    if (!reviewPost) return
    
    approvePostMutation.mutate({
      postId: reviewPost.id,
      approval: {
        approvalStatus: approval,
        approvalNotes: reviewNotes.trim() || undefined
      }
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-4">
          <div className="w-8 h-8 border-2 border-green-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-green-700 font-medium">Lade generierte Inhalte...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6" data-testid="content-library">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" data-testid="page-title">
            Inhalts-Bibliothek
          </h1>
          <p className="text-gray-600 mt-2">
            Verwalten Sie Ihre KI-Beiträge für alle Plattformen
          </p>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Gesamt Posts</p>
                <p className="text-2xl font-bold">{generatedPosts.length}</p>
              </div>
              <MessageSquare className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Ausstehend</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {generatedPosts.filter((p) => p.approvalStatus === "pending").length}
                </p>
              </div>
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Genehmigt</p>
                <p className="text-2xl font-bold text-green-600">
                  {generatedPosts.filter((p) => p.approvalStatus === "approved").length}
                </p>
              </div>
              <ThumbsUp className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Veröffentlicht</p>
                <p className="text-2xl font-bold text-blue-600">
                  {generatedPosts.filter((p) => p.postingStatus === "posted").length}
                </p>
              </div>
              <ExternalLink className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Select value={selectedPlatform} onValueChange={setSelectedPlatform}>
          <SelectTrigger className="w-full sm:w-48" data-testid="platform-filter">
            <SelectValue placeholder="Plattform wählen" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Plattformen</SelectItem>
            {Object.entries(platformConfig).map(([key, config]) => (
              <SelectItem key={key} value={key}>
                {config.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
          <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
            <SelectValue placeholder="Status wählen" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Status</SelectItem>
            <SelectItem value="pending">Ausstehend</SelectItem>
            <SelectItem value="approved">Genehmigt</SelectItem>
            <SelectItem value="rejected">Abgelehnt</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Übersicht</TabsTrigger>
          <TabsTrigger value="posts" data-testid="tab-posts">Alle Posts</TabsTrigger>
          <TabsTrigger value="repurposing" data-testid="tab-repurposing">Repurposing</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {Object.entries(postsByPlatform).map(([platform, posts]) => {
            const config = platformConfig[platform as keyof typeof platformConfig]
            if (!config) return null

            return (
              <Card key={platform}>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${config.color}`}>
                      <config.icon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{config.name}</CardTitle>
                      <CardDescription>
                        {posts.length} Posts • {posts.filter((p: GeneratedPost) => p.approvalStatus === "pending").length} ausstehend
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {(posts as GeneratedPost[]).slice(0, 6).map((post) => (
                      <PostPreviewCard 
                        key={post.id} 
                        post={post} 
                        onReview={setReviewPost}
                      />
                    ))}
                  </div>
                  {posts.length > 6 && (
                    <div className="mt-4 text-center">
                      <Button variant="outline" size="sm" onClick={() => setSelectedPlatform(platform)}>
                        Alle {posts.length} Posts anzeigen
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </TabsContent>

        <TabsContent value="posts" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPosts.map((post) => (
              <PostCard 
                key={post.id} 
                post={post} 
                onReview={setReviewPost}
              />
            ))}
          </div>

          {filteredPosts.length === 0 && (
            <Card className="p-8 text-center">
              <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Keine Posts gefunden</h3>
              <p className="text-gray-500">
                Es wurden keine Posts mit den ausgewählten Filtern gefunden.
              </p>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="repurposing" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPosts
              .filter(post => post.sourceType === 'repurposing_content')
              .map((post) => (
                <PostCard 
                  key={post.id} 
                  post={post} 
                  onReview={setReviewPost}
                />
              ))}
          </div>

          {filteredPosts.filter(post => post.sourceType === 'repurposing_content').length === 0 && (
            <Card className="p-8 text-center">
              <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Keine Repurposing Posts gefunden</h3>
              <p className="text-gray-500">
                Es wurden noch keine Posts aus YouTube-Videos oder Artikeln generiert.
              </p>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Review Dialog */}
      <Dialog open={!!reviewPost} onOpenChange={() => setReviewPost(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Post überprüfen</DialogTitle>
            <DialogDescription>
              Prüfen Sie diesen Beitrag und genehmigen oder verwerfen Sie ihn.
            </DialogDescription>
          </DialogHeader>
          
          {reviewPost && <PostReviewContent post={reviewPost} />}
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Notizen (optional)
              </label>
              <Textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Optionale Notizen zur Freigabe oder Ablehnung"
                rows={3}
                data-testid="review-notes"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setReviewPost(null)}
              data-testid="button-cancel-review"
            >
              Abbrechen
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleApprovePost("rejected")}
              disabled={approvePostMutation.isPending}
              data-testid="button-reject-post"
            >
              <X className="h-4 w-4 mr-2" />
              Ablehnen
            </Button>
            <Button 
              onClick={() => handleApprovePost("approved")}
              disabled={approvePostMutation.isPending}
              data-testid="button-approve-post"
            >
              <Check className="h-4 w-4 mr-2" />
              Genehmigen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function PostPreviewCard({ post, onReview }: { post: GeneratedPost; onReview: (post: GeneratedPost) => void }) {
  const platform = platformConfig[post.platform as keyof typeof platformConfig]
  const status = statusConfig[post.approvalStatus as keyof typeof statusConfig]
  
  return (
    <Card className="hover-elevate cursor-pointer transition-all" onClick={() => onReview(post)}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <Badge className={status.color}>
            {status.label}
          </Badge>
          <div className="text-xs text-gray-500">
            {post.createdAt ? format(new Date(post.createdAt), "dd.MM.yy", { locale: de }) : "--"}
          </div>
        </div>
        
        <div className="space-y-2">
          <p className="text-sm text-gray-600 line-clamp-3">
            {post.postContent}
          </p>
          
          {post.hashtags && post.hashtags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {post.hashtags.slice(0, 3).map((tag, index) => (
                <span key={index} className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                  #{tag}
                </span>
              ))}
              {post.hashtags.length > 3 && (
                <span className="text-xs text-gray-500">+{post.hashtags.length - 3} mehr</span>
              )}
            </div>
          )}
        </div>

        <Button variant="ghost" size="sm" className="w-full">
          <Eye className="h-4 w-4 mr-2" />
          Details ansehen
        </Button>
      </CardContent>
    </Card>
  )
}

function PostCard({ post, onReview }: { post: GeneratedPost; onReview: (post: GeneratedPost) => void }) {
  const config = platformConfig[post.platform as keyof typeof platformConfig]
  const status = statusConfig[post.approvalStatus as keyof typeof statusConfig]
  const postingStatus = statusConfig[post.postingStatus as keyof typeof statusConfig]
  
  if (!config) return null

  return (
    <Card className="hover-elevate" data-testid={`post-card-${post.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${config.color}`}>
              <config.icon className="h-4 w-4 text-white" />
            </div>
            <div>
              <h3 className="font-medium">{config.name}</h3>
              <p className="text-sm text-gray-500">
                {post.createdAt ? format(new Date(post.createdAt), "dd.MM.yyyy HH:mm", { locale: de }) : "--"}
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-1 items-end">
            <Badge className={status.color}>
              {status.label}
            </Badge>
            <Badge variant="secondary" className="text-xs">
              {postingStatus.label}
            </Badge>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {post.postTitle && (
          <h4 className="font-medium text-gray-900">{post.postTitle}</h4>
        )}
        
        <p className="text-sm text-gray-600 line-clamp-4">
          {post.postContent}
        </p>
        
        {post.hashtags && post.hashtags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            <Hash className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
            <div className="flex flex-wrap gap-1">
              {post.hashtags.map((tag, index) => (
                <span key={index} className="text-sm text-blue-600">
                  {tag}{index < (post.hashtags?.length || 0) - 1 ? ", " : ""}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-between items-center pt-2">
          <div className="text-xs text-gray-500">
            {post.approvedAt && (
              <span>
                Genehmigt: {format(new Date(post.approvedAt), "dd.MM.yy", { locale: de })}
              </span>
            )}
          </div>
          
          {post.approvalStatus === "pending" && (
            <Button size="sm" onClick={() => onReview(post)} data-testid={`review-button-${post.id}`}>
              <Eye className="h-4 w-4 mr-2" />
              Überprüfen
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function PostReviewContent({ post }: { post: GeneratedPost }) {
  const config = platformConfig[post.platform as keyof typeof platformConfig]
  
  if (!config) return null

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className={`p-3 rounded-lg ${config.color}`}>
          <config.icon className="h-6 w-6 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold">{config.name}</h3>
          <p className="text-sm text-gray-500">
            Erstellt: {post.createdAt ? format(new Date(post.createdAt), "dd.MM.yyyy HH:mm", { locale: de }) : "--"}
          </p>
        </div>
      </div>

      {post.postTitle && (
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">Titel</label>
          <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{post.postTitle}</p>
        </div>
      )}
      
      <div>
        <label className="text-sm font-medium text-gray-700 mb-1 block">Post Inhalt</label>
        <div className="bg-gray-50 p-4 rounded-lg">
          <p className="text-gray-900 whitespace-pre-wrap">{post.postContent}</p>
        </div>
      </div>
      
      {post.hashtags && post.hashtags.length > 0 && (
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">Hashtags</label>
          <div className="flex flex-wrap gap-2">
            {post.hashtags.map((tag, index) => (
              <Badge key={index} variant="secondary" className="text-blue-600 bg-blue-50">
                #{tag}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {post.imageContent && (
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">Bild</label>
          <div className="bg-gray-50 p-4 rounded-lg">
            {post.imageContent.startsWith('data:image') ? (
              <img 
                src={post.imageContent} 
                alt="Generated post image"
                className="max-w-full max-h-64 object-contain rounded-lg"
              />
            ) : (
              <p className="text-gray-600 text-sm">Bild-URL: {post.imageContent}</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}